import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Zap, Droplets, Wrench, Sparkles, Truck, ChevronRight, ChevronLeft, Bot, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const HeroSection = ({ 
  searchQuery, 
  setSearchQuery, 
  showSuggestions, 
  setShowSuggestions, 
  filteredSuggestions,
  searchRef 
}) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeCategory, setActiveCategory] = useState(0);
  const [currentUstaSet, setCurrentUstaSet] = useState(0);

  // Türk ustalarına benzeyen resimler - 3'erli setler halinde değişecek
  const ustaImages = [
    // Set 1
    [
      "https://images.unsplash.com/photo-1762066436595-67edb4610539?w=150&h=150&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1643438430812-25ba3d570caa?w=150&h=150&fit=crop&crop=face",
      "https://images.pexels.com/photos/16552851/pexels-photo-16552851.jpeg?w=150&h=150&fit=crop"
    ],
    // Set 2
    [
      "https://images.pexels.com/photos/9679179/pexels-photo-9679179.jpeg?w=150&h=150&fit=crop",
      "https://images.unsplash.com/photo-1609444074870-2860a9a613e3?w=150&h=150&fit=crop&crop=face",
      "https://images.unsplash.com/photo-1721003637660-e9d3aab9083b?w=150&h=150&fit=crop&crop=face"
    ],
    // Set 3
    [
      "https://images.unsplash.com/photo-1721006779304-49a9f5beadab?w=150&h=150&fit=crop&crop=face",
      "https://images.pexels.com/photos/3658574/pexels-photo-3658574.jpeg?w=150&h=150&fit=crop",
      "https://images.unsplash.com/photo-1762066436595-67edb4610539?w=150&h=150&fit=crop&crop=face"
    ]
  ];

  // Desktop slider images - Ev ortamında servis resimleri
  const sliderImages = [
    {
      url: "https://images.unsplash.com/photo-1513694203232-719a280e022f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
      title: "Kombi Servisi",
      subtitle: "Profesyonel bakım ve onarım"
    },
    {
      url: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
      title: "Klima Servisi",
      subtitle: "Montaj ve bakım hizmetleri"
    },
    {
      url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
      title: "Beyaz Eşya Servisi",
      subtitle: "Tüm marka ve modeller"
    }
  ];

  const categories = [
    { id: 'kombi', name: 'Kombi', icon: Wrench, color: '#FF6B35', emoji: '🔧' },
    { id: 'klima', name: 'Klima', icon: Sparkles, color: '#00D4FF', emoji: '❄️' },
    { id: 'elektrik', name: 'Elektrik', icon: Zap, color: '#FFD700', emoji: '⚡' },
    { id: 'su_tesisati', name: 'Tesisat', icon: Droplets, color: '#4A90D9', emoji: '🚿' },
    { id: 'beyaz_esya', name: 'Beyaz Eşya', icon: Truck, color: '#9B59B6', emoji: '🧺' },
  ];

  // Auto rotate categories
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCategory((prev) => (prev + 1) % categories.length);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  // Auto rotate usta images
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentUstaSet((prev) => (prev + 1) % ustaImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Desktop slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate('/hizmetler');
    }
  };

  const handleSuggestionClick = (serviceId) => {
    setShowSuggestions(false);
    navigate(`/servis-talep/${serviceId}`);
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);

  return (
    <>
      {/* ========== MOBILE HERO ========== */}
      <section className="lg:hidden min-h-screen relative overflow-hidden">
        
        {/* Animated Background */}
        <div className="absolute inset-0 bg-[#0A0E1A]">
          {/* Gradient Orbs */}
          <div className="absolute top-1/4 -left-20 w-72 h-72 bg-orange-500/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-blue-500/15 rounded-full blur-[100px] animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px]" />
          
          {/* Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col min-h-screen px-6 pt-16">
          
          {/* AI Badge */}
          <div className="flex justify-center mb-4 mt-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-purple-500/20 backdrop-blur-xl border border-white/10 rounded-full px-4 py-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <Bot className="w-4 h-4 text-orange-400" />
              <span className="text-white/80 text-xs font-medium">Yapay Zeka Destekli Fiyat Tahmini</span>
            </div>
          </div>

          {/* Dynamic Headline - Aktif kategoriye göre değişiyor */}
          <div className="text-center mb-6">
            <h1 className="text-4xl font-black text-white mb-3 leading-tight">
              Aradığın{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500">
                {categories[activeCategory].name}
              </span>
              <br />
              ustası burada
            </h1>
            <p className="text-white/50 text-sm">
              Saniyeler içinde teklif al, karşılaştır, seç.
            </p>
          </div>

          {/* Search Box */}
          <div ref={searchRef} className="relative z-50 mb-8">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/50 to-purple-500/50 rounded-3xl blur-lg opacity-50" />
              <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 overflow-hidden">
                <div className="flex items-center">
                  <Search className="ml-5 text-white/40 w-6 h-6" />
                  <Input
                    type="text"
                    placeholder={`${categories[activeCategory].name} servisi arıyorum...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1 px-4 py-7 border-0 text-white bg-transparent placeholder:text-white/40 focus:ring-0 focus:outline-none text-lg"
                    data-testid="search-input"
                  />
                  <Button
                    onClick={handleSearch}
                    className="m-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 rounded-2xl shadow-lg shadow-orange-500/25"
                  >
                    <ArrowRight className="w-6 h-6" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Suggestions */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-3 bg-[#151B2E]/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden z-50">
                <div className="p-2">
                  {filteredSuggestions.map((suggestion) => {
                    const Icon = suggestion.icon;
                    return (
                      <button
                        key={suggestion.id}
                        onClick={() => handleSuggestionClick(suggestion.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-xl transition-all text-left"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center">
                          <Icon className="w-5 h-5 text-orange-400" />
                        </div>
                        <span className="text-white font-medium flex-1">{suggestion.name}</span>
                        <ChevronRight className="w-4 h-4 text-white/30" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="flex justify-center gap-3 mb-6">
            {categories.map((cat, index) => {
              const Icon = cat.icon;
              const isActive = index === activeCategory;
              return (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/servis-talep/${cat.id}`)}
                  className={`flex flex-col items-center gap-1.5 transition-all duration-300 ${isActive ? 'scale-110' : 'scale-100 opacity-60'}`}
                  data-testid={`category-${cat.id}`}
                >
                  <div 
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      isActive 
                        ? 'shadow-lg' 
                        : ''
                    }`}
                    style={{ 
                      background: isActive 
                        ? `linear-gradient(135deg, ${cat.color}40, ${cat.color}20)` 
                        : 'rgba(255,255,255,0.05)',
                      border: isActive ? `1px solid ${cat.color}50` : '1px solid rgba(255,255,255,0.1)',
                      boxShadow: isActive ? `0 8px 32px ${cat.color}30` : 'none'
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: isActive ? cat.color : 'rgba(255,255,255,0.5)' }} />
                  </div>
                  <div className="text-center leading-tight">
                    <span className={`text-xs font-medium transition-all block ${isActive ? 'text-white' : 'text-white/40'}`}>
                      {cat.name}
                    </span>
                    <span className={`text-[10px] transition-all ${isActive ? 'text-white/60' : 'text-white/30'}`}>
                      Servisi
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="pb-8 pt-4">
            {/* Two Equal Buttons Row */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {/* CTA Button - Talep ile Usta Çağır */}
              <Button
                onClick={() => navigate('/hizmetler')}
                className="h-14 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-sm rounded-2xl shadow-xl shadow-orange-500/30"
                data-testid="create-request-btn"
              >
                <span className="flex items-center justify-center gap-1.5">
                  <ArrowRight className="w-4 h-4" />
                  Talep ile Usta Çağır
                </span>
              </Button>
              
              {/* WhatsApp Button */}
              <a
                href="https://wa.me/908502427808?text=Merhaba,%20usta%20hizmeti%20almak%20istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                className="h-14 flex items-center justify-center gap-1.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-sm rounded-2xl shadow-xl shadow-green-500/30 transition-all"
                data-testid="whatsapp-btn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp ile Çağır
              </a>
            </div>
            
            {/* Phone Button - Different Color Below */}
            <a
              href="tel:+908502427808"
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold text-sm py-4 rounded-2xl shadow-xl shadow-blue-500/30 mb-4 transition-all"
              data-testid="phone-btn"
            >
              <Phone className="w-4 h-4" />
              Hemen Ara, Usta Gelsin
            </a>

            {/* Avantajlar */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 mb-4">
              <div className="flex justify-around">
                <div className="text-center">
                  <div className="text-2xl mb-1">⚡</div>
                  <div className="text-white text-xs font-semibold">Hızlı</div>
                  <div className="text-white/40 text-[10px]">15 dk'da teklif</div>
                </div>
                <div className="w-px bg-white/10" />
                <div className="text-center">
                  <div className="text-2xl mb-1">🛡️</div>
                  <div className="text-white text-xs font-semibold">Garantili</div>
                  <div className="text-white/40 text-[10px]">1 yıl garanti</div>
                </div>
                <div className="w-px bg-white/10" />
                <div className="text-center">
                  <div className="text-2xl mb-1">💰</div>
                  <div className="text-white text-xs font-semibold">Uygun</div>
                  <div className="text-white/40 text-[10px]">En iyi fiyat</div>
                </div>
              </div>
            </div>
            
            {/* Trust Badge - Büyük ve Belirgin */}
            <div className="bg-gradient-to-r from-orange-500/10 to-green-500/10 backdrop-blur-sm rounded-2xl border border-white/20 p-4">
              <div className="flex justify-center items-center gap-5">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {ustaImages[currentUstaSet].map((img, idx) => (
                      <img 
                        key={idx}
                        src={img} 
                        className="w-10 h-10 rounded-full border-2 border-[#0A0E1A] object-cover shadow-lg transition-all duration-500" 
                        alt="Usta" 
                      />
                    ))}
                  </div>
                  <span className="text-white text-lg font-bold whitespace-nowrap">500+ Usta</span>
                </div>
                <div className="w-px h-8 bg-white/30" />
                <div className="flex items-center gap-1.5">
                  <span className="text-yellow-400 text-2xl">★</span>
                  <span className="text-white text-lg font-bold whitespace-nowrap">4.9 Puan</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== DESKTOP HERO ========== */}
      <section className="hidden lg:block relative min-h-[85vh] overflow-hidden">
        {/* Slider */}
        <div className="absolute inset-0">
          {sliderImages.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img src={slide.url} alt={slide.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/40" />
            </div>
          ))}
        </div>

        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white/30'}`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="absolute top-32 left-0 right-0 z-10 px-8">
          <div className="w-full max-w-7xl mx-auto">
            <div className="lg:w-3/5 xl:w-1/2">

              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
                <Bot className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Yapay Zeka Destekli Fiyat Tahmini</span>
              </div>

              <h1 className="text-4xl xl:text-5xl font-black text-white mb-3 leading-tight">
                <span className="block">Aradığın</span>
                <span className="block text-orange-500">{categories[activeCategory].name} Ustası</span>
              </h1>
              
              <p className="text-slate-300 text-lg mb-4 max-w-md">
                Kombi, klima, beyaz eşya için <strong className="text-white">uzman ustalar</strong> bir tık uzağınızda.
              </p>

              <div className="mb-4 bg-white/5 rounded-xl px-4 py-2 border border-white/10 inline-block">
                <h3 className="text-white font-bold text-base">{sliderImages[currentSlide].title}</h3>
                <p className="text-white/60 text-sm">{sliderImages[currentSlide].subtitle}</p>
              </div>

              <div ref={searchRef} className="relative z-50 w-full max-w-2xl mb-4">
                <div className="bg-white rounded-3xl shadow-2xl p-3">
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-7 h-7" />
                      <Input
                        type="text"
                        placeholder={`${categories[activeCategory].name} servisi arıyorum...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setShowSuggestions(true)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        className="w-full pl-16 pr-4 py-5 border-0 text-slate-700 text-xl focus:ring-0 bg-slate-50 rounded-2xl placeholder:text-slate-400"
                      />
                    </div>
                    <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-5 rounded-2xl font-bold text-lg shadow-lg shadow-orange-500/30">
                      Ara
                    </Button>
                  </div>
                </div>

                {showSuggestions && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border overflow-hidden z-50">
                    <div className="p-3">
                      {filteredSuggestions.map((suggestion) => {
                        const Icon = suggestion.icon;
                        return (
                          <button
                            key={suggestion.id}
                            onClick={() => handleSuggestionClick(suggestion.id)}
                            className="w-full flex items-center gap-4 px-4 py-3 hover:bg-slate-50 rounded-xl text-left"
                          >
                            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                              <Icon className="w-6 h-6 text-orange-500" />
                            </div>
                            <span className="text-slate-800 font-semibold flex-1">{suggestion.name}</span>
                            <ChevronRight className="w-5 h-5 text-slate-300" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => navigate(`/servis-talep/${cat.id}`)}
                      className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-3 py-2"
                    >
                      <Icon className="w-3.5 h-3.5 text-orange-400" />
                      <span className="text-white text-xs font-medium">{cat.name} Servisi</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
